import { useCallback, useEffect, useRef, useState, type DragEvent } from 'react'
import {
  Upload,
  FileText,
  ChevronDown,
  ChevronRight,
  Download,
  Trash2,
  Loader2,
  Plus,
  RefreshCw,
  Sparkles,
  Tag as TagIcon,
} from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import Modal from '../ui/Modal'
import AiResultModal from '../ui/AiResultModal'
import DocIndexBadge from './DocIndexBadge'
import ApplyTemplateModal from './ApplyTemplateModal'
import {
  DocumentCategoryLabel,
  type AiSummaryResponse,
  type DocumentCategory,
  type DocumentDetail,
  type DocumentListItem,
} from '@toga/shared'

interface Props {
  caseId: string
  canWrite: boolean
  onChanged?: () => void
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default function DocumentsPanel({ caseId, canWrite, onChanged }: Props) {
  const [docs, setDocs] = useState<DocumentListItem[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [expandedDetail, setExpandedDetail] = useState<DocumentDetail | null>(null)
  const [summarizingId, setSummarizingId] = useState<string | null>(null)
  const [reindexingId, setReindexingId] = useState<string | null>(null)
  const [showApplyTemplate, setShowApplyTemplate] = useState(false)
  const [aiResult, setAiResult] = useState<{
    title: string
    text: string
    conversationId: string | null
  } | null>(null)

  const reload = useCallback(async () => {
    try {
      const { items } = await api.get<{ items: DocumentListItem[] }>(`/documents?caseId=${caseId}`)
      setDocs(items)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar documentos')
    }
  }, [caseId])

  useEffect(() => {
    void reload()
  }, [reload])

  useEffect(() => {
    if (!expandedId) {
      setExpandedDetail(null)
      return
    }
    void (async () => {
      try {
        const { document } = await api.get<{ document: DocumentDetail }>(`/documents/${expandedId}`)
        setExpandedDetail(document)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error al cargar versiones')
      }
    })()
  }, [expandedId])

  const onDelete = async (d: DocumentListItem) => {
    if (!confirm(`¿Eliminar el documento "${d.fileName}"? (Borrado lógico)`)) return
    try {
      await api.delete(`/documents/${d.id}`)
      await reload()
      onChanged?.()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error')
    }
  }

  const onReindex = async (d: DocumentListItem) => {
    setReindexingId(d.id)
    setError(null)
    try {
      await api.post(`/documents/${d.id}/reindex`, {})
      await reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al reindexar')
    } finally {
      setReindexingId(null)
    }
  }

  const onSummarize = async (d: DocumentListItem) => {
    setSummarizingId(d.id)
    setError(null)
    try {
      const resp = await api.post<AiSummaryResponse>('/ai/summary', {
        contextType: 'DOCUMENT',
        contextId: d.id,
        style: 'BRIEF',
      })
      setAiResult({
        title: `Resumen — ${d.fileName}`,
        text: resp.text,
        conversationId: resp.conversationId,
      })
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string; code?: string } | undefined
        if (body?.code === 'AI_NOT_CONFIGURED') {
          setError('Asistente IA no configurado en el servidor.')
        } else {
          setError(body?.error ?? err.message)
        }
      } else {
        setError(err instanceof Error ? err.message : 'Error al resumir')
      }
    } finally {
      setSummarizingId(null)
    }
  }

  const onDownload = async (documentId: string, version: number) => {
    try {
      const { url } = await api.get<{ url: string }>(
        `/documents/${documentId}/versions/${version}/download`
      )
      window.open(url, '_blank')
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error al generar descarga')
    }
  }

  const handleFiles = async (files: FileList) => {
    if (files.length === 0) return
    const file = files[0]
    if (!file) return
    setUploadOpen(true)
    pendingFileRef.current = file
  }

  const pendingFileRef = useRef<File | null>(null)

  const onDrop = (e: DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    if (!canWrite) return
    if (e.dataTransfer.files.length > 0) void handleFiles(e.dataTransfer.files)
  }

  return (
    <div
      className={[
        'surface p-5 relative transition',
        dragOver ? 'ring-2 ring-toga-500/60' : '',
      ].join(' ')}
      onDragOver={(e) => {
        if (!canWrite) return
        e.preventDefault()
        setDragOver(true)
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="t-primary font-semibold flex items-center gap-2">
          <FileText size={16} className="text-toga-300" />
          Documentos
          {docs && <span className="text-xs t-muted font-normal">({docs.length})</span>}
        </h3>
        {canWrite && (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setShowApplyTemplate(true)}
              className="px-2.5 py-1 rounded-lg text-xs font-medium t-secondary border border-base hover:bg-hover flex items-center gap-1"
              title="Generar documento a partir de una plantilla"
            >
              <FileText size={12} /> Plantilla
            </button>
            <button
              type="button"
              onClick={() => {
                pendingFileRef.current = null
                setUploadOpen(true)
              }}
              className="px-2.5 py-1 rounded-lg text-xs font-medium text-toga-200 bg-toga-500/15 hover:bg-toga-500/25 flex items-center gap-1"
            >
              <Plus size={14} /> Subir
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="text-sm rounded-lg px-3 py-2 border text-rose-300 bg-rose-500/10 border-rose-500/30 mb-3">
          {error}
        </div>
      )}

      {canWrite && (!docs || docs.length === 0) && (
        <div className="text-center py-10 border-2 border-dashed border-base rounded-xl">
          <Upload className="mx-auto t-faint mb-2" size={28} />
          <p className="t-muted text-sm">
            Arrastra un archivo aquí o pulsa "Subir".
            <br />
            <span className="text-xs t-faint">Tamaño máximo: 50 MB</span>
          </p>
        </div>
      )}

      {docs && docs.length > 0 && (
        <ul className="divide-y divide-base/50">
          {docs.map((d) => {
            const isExpanded = expandedId === d.id
            return (
              <li key={d.id} className="py-2.5">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setExpandedId(isExpanded ? null : d.id)}
                    className="w-7 h-7 grid place-items-center rounded hover:bg-hover t-muted"
                  >
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </button>
                  <FileText size={18} className="text-toga-300 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="t-primary text-sm font-medium truncate">{d.fileName}</div>
                    <div className="t-faint text-xs flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="px-1.5 py-0.5 rounded bg-slate-800/70 text-slate-300">
                        {DocumentCategoryLabel[d.category]}
                      </span>
                      <span>v{d.latestVersion}</span>
                      <span>·</span>
                      <span>{formatSize(d.latestSize)}</span>
                      <DocIndexBadge status={d.indexStatus} />
                      {d.tags.length > 0 && (
                        <span className="inline-flex items-center gap-1">
                          <TagIcon size={10} />
                          {d.tags.join(', ')}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => void onSummarize(d)}
                    disabled={summarizingId !== null}
                    className="w-8 h-8 grid place-items-center rounded-lg hover:bg-hover t-muted hover:text-toga-300 disabled:opacity-40"
                    title="Resumir con IA"
                  >
                    {summarizingId === d.id ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Sparkles size={14} />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => onDownload(d.id, d.latestVersion)}
                    className="w-8 h-8 grid place-items-center rounded-lg hover:bg-hover t-muted hover:text-toga-300"
                    title="Descargar última versión"
                  >
                    <Download size={14} />
                  </button>
                  {canWrite && (
                    <button
                      type="button"
                      onClick={() => void onReindex(d)}
                      disabled={reindexingId !== null}
                      className="w-8 h-8 grid place-items-center rounded-lg hover:bg-hover t-muted hover:text-toga-300 disabled:opacity-40"
                      title="Reindexar para búsqueda semántica"
                    >
                      {reindexingId === d.id ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <RefreshCw size={14} />
                      )}
                    </button>
                  )}
                  {canWrite && (
                    <button
                      type="button"
                      onClick={() => onDelete(d)}
                      className="w-8 h-8 grid place-items-center rounded-lg hover:bg-rose-500/10 t-muted hover:text-rose-400"
                      title="Eliminar"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>

                {isExpanded && (
                  <div className="pl-10 mt-2 mb-1 space-y-1.5">
                    {!expandedDetail ? (
                      <div className="t-muted text-xs flex items-center gap-2">
                        <Loader2 size={12} className="animate-spin" /> Cargando versiones…
                      </div>
                    ) : (
                      <>
                        {expandedDetail.versions.map((v) => (
                          <div key={v.id} className="flex items-center gap-2 text-xs t-muted py-1">
                            <span className="font-mono w-8">v{v.version}</span>
                            <span className="flex-1">
                              {new Date(v.uploadedAt).toLocaleString('es-ES')} ·{' '}
                              {v.uploadedByName ?? 'desconocido'}
                            </span>
                            <span>{formatSize(v.size)}</span>
                            <button
                              type="button"
                              onClick={() => onDownload(d.id, v.version)}
                              className="px-2 py-0.5 rounded text-toga-300 hover:bg-hover"
                            >
                              <Download size={12} />
                            </button>
                          </div>
                        ))}
                        {canWrite && (
                          <UploadNewVersion
                            documentId={d.id}
                            onUploaded={async () => {
                              await reload()
                              const { document } = await api.get<{ document: DocumentDetail }>(
                                `/documents/${d.id}`
                              )
                              setExpandedDetail(document)
                            }}
                          />
                        )}
                      </>
                    )}
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {aiResult && (
        <AiResultModal
          title={aiResult.title}
          text={aiResult.text}
          conversationId={aiResult.conversationId}
          onClose={() => setAiResult(null)}
        />
      )}

      {showApplyTemplate && (
        <ApplyTemplateModal
          caseId={caseId}
          onClose={() => setShowApplyTemplate(false)}
          onApplied={async () => {
            setShowApplyTemplate(false)
            await reload()
            onChanged?.()
          }}
        />
      )}

      {uploadOpen && (
        <UploadModal
          caseId={caseId}
          initialFile={pendingFileRef.current}
          onClose={() => {
            setUploadOpen(false)
            pendingFileRef.current = null
          }}
          onUploaded={async () => {
            setUploadOpen(false)
            pendingFileRef.current = null
            await reload()
            onChanged?.()
          }}
        />
      )}
    </div>
  )
}

function UploadNewVersion({
  documentId,
  onUploaded,
}: {
  documentId: string
  onUploaded: () => Promise<void> | void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setBusy(true)
    try {
      const form = new FormData()
      form.append('file', file)
      await api.upload(`/documents/${documentId}/versions`, form)
      await onUploaded()
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? ((err.body as { error?: string } | undefined)?.error ?? err.message)
          : err instanceof Error
            ? err.message
            : 'Error al subir'
      alert(msg)
    } finally {
      setBusy(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  return (
    <label className="text-xs text-toga-300 hover:underline cursor-pointer inline-flex items-center gap-1">
      {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
      Subir nueva versión
      <input ref={inputRef} type="file" className="hidden" onChange={onPick} disabled={busy} />
    </label>
  )
}

/** Sube un documento a un expediente (`caseId`) o, sin expediente, al propio cliente (`clientId`). */
export function UploadModal({
  caseId,
  clientId,
  initialFile,
  onClose,
  onUploaded,
}: {
  caseId?: string
  clientId?: string
  initialFile: File | null
  onClose: () => void
  onUploaded: () => Promise<void> | void
}) {
  const [file, setFile] = useState<File | null>(initialFile)
  const [category, setCategory] = useState<DocumentCategory>('OTRO')
  const [tagsRaw, setTagsRaw] = useState('')
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!file) return setError('Selecciona un archivo')
    setError(null)
    setBusy(true)
    try {
      const form = new FormData()
      form.append('file', file)
      if (caseId) form.append('caseId', caseId)
      if (clientId) form.append('clientId', clientId)
      form.append('category', category)
      if (tagsRaw.trim()) form.append('tags', tagsRaw.trim())
      if (notes.trim()) form.append('notes', notes.trim())
      await api.upload('/documents', form)
      await onUploaded()
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined
        setError(body?.error ?? err.message)
      } else {
        setError(err instanceof Error ? err.message : 'Error al subir')
      }
    } finally {
      setBusy(false)
    }
  }

  const inputCls =
    'w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700/60 text-slate-100 focus:outline-none focus:ring-1 focus:ring-toga-500/40 focus:border-toga-500/60'

  return (
    <Modal open onClose={onClose} title="Subir documento" maxWidth="max-w-lg">
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="block text-xs t-muted mb-1.5">Archivo</label>
          <input
            type="file"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="w-full text-sm t-secondary file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-toga-500/15 file:text-toga-200 file:cursor-pointer"
          />
          {file && (
            <div className="text-xs t-faint mt-1">
              {file.name} · {formatSize(file.size)}
            </div>
          )}
        </div>
        <div>
          <label className="block text-xs t-muted mb-1.5">Categoría</label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as DocumentCategory)}
            className={inputCls}
          >
            {Object.entries(DocumentCategoryLabel).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs t-muted mb-1.5">Etiquetas (separadas por coma)</label>
          <input
            value={tagsRaw}
            onChange={(e) => setTagsRaw(e.target.value)}
            placeholder="urgente, primera-instancia"
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-xs t-muted mb-1.5">Notas (opcional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className={inputCls}
          />
        </div>

        {error && (
          <div className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm bg-slate-800/60 hover:bg-slate-800 t-secondary"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={busy || !file}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 hover:shadow-glow-toga disabled:opacity-50 flex items-center gap-2"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            Subir
          </button>
        </div>
      </form>
    </Modal>
  )
}
